import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaRegThumbsUp } from "react-icons/fa6";
import { useTranslation } from "react-i18next";
import useAuth from "../../hooks/useAuth";
import { ASSET_URI, PROXY } from "../../lib/utils";

export default function LikedCourses() {
  const { t, i18n } = useTranslation();
  const { accessToken } = useAuth();
  const [courses, setCourses] = useState([]);
  const [state, setState] = useState("loading");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    const controller = new AbortController();
    setState("loading");
    fetch(`${PROXY}/courses/liked?page=${page}&limit=8`, { signal: controller.signal, headers: { Authorization: `Bearer ${accessToken}` } })
      .then((response) => {
        if (!response.ok) throw new Error("Unable to load liked courses");
        return response.json();
      })
      .then((data) => { if (!controller.signal.aborted) { setCourses(data.courses || []); setTotalPages(data.totalPages || 1); setState("ready"); } })
      .catch(() => { if (!controller.signal.aborted) setState("error"); });
    return () => controller.abort();
  }, [accessToken, page]);

  if (state === "loading") return <p aria-live="polite" className="text-sm text-gray-500">{t("Loading", "Loading...")}</p>;
  if (state === "error") return <p role="alert" className="text-sm text-red-600">{t("courseSearch.error")}</p>;
  if (!courses.length) return <p className="rounded-lg bg-gray-50 p-4 text-sm text-gray-600">{t("No-liked-courses", "You have not liked any courses yet.")}</p>;

  return <div dir={i18n.language === "en" ? "ltr" : "rtl"}>
    <div className="grid gap-4 sm:grid-cols-2">
      {courses.map((course) => <Link key={course._id} to={`/courses/${course._id}`} className="flex min-w-0 gap-3 rounded-xl border p-3 transition hover:border-indigo-300 hover:bg-indigo-50 focus:ring-2 focus:ring-indigo-500">
      {course.image ? <img src={`${ASSET_URI}/${course.image}`} alt="" className="h-14 w-14 shrink-0 rounded-lg object-cover" loading="lazy" crossOrigin="anonymous" /> : <div className="h-14 w-14 shrink-0 rounded-lg bg-gray-100" />}
      <div className="min-w-0">
        <p className="truncate font-semibold text-gray-900">{course.title?.[i18n.language] || course.title?.en || course.title?.pr || course.title?.ps}</p>
        <p className="mt-1 inline-flex items-center gap-1 text-xs text-blue-700"><FaRegThumbsUp aria-hidden="true" /> {course.likesCount}</p>
      </div>
      </Link>)}
    </div>
    {totalPages > 1 && <nav className="mt-5 flex items-center justify-center gap-4" aria-label={t("courseSearch.pagination", "Liked course pages")}>
      <button type="button" disabled={page === 1} onClick={() => setPage((current) => current - 1)} className="rounded-lg border px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50 focus:ring-2 focus:ring-indigo-500">{t("courseSearch.previous", "Previous")}</button>
      <span aria-live="polite" className="text-sm text-gray-600">{page} / {totalPages}</span>
      <button type="button" disabled={page === totalPages} onClick={() => setPage((current) => current + 1)} className="rounded-lg border px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50 focus:ring-2 focus:ring-indigo-500">{t("courseSearch.next", "Next")}</button>
    </nav>}
  </div>;
}
